import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Container,
} from '@mui/material';
import {
  LocationCity,
  Train,
  Place,
  AccountTree,
  Timeline,
  LocalHospital,
  Accessibility,
  Route,
  Analytics,
  Add,
  Edit,
  Delete,
  SystemUpdate,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // Mock data - replace with actual API calls
  const stats = {
    cities: 3,
    lines: 8,
    stations: 45,
    connections: 120,
    facilities: 89,
    amenities: 156,
    appVersions: 2,
  };

  const quickActions = [
    { title: 'Add City', icon: <LocationCity />, path: '/cities', color: '#1976d2' },
    { title: 'Add Line', icon: <Train />, path: '/lines', color: '#dc004e' },
    { title: 'Add Station', icon: <Place />, path: '/stations', color: '#2e7d32' },
    { title: 'Add Connection', icon: <AccountTree />, path: '/station-connections', color: '#ed6c02' },
    { title: 'Plan Route', icon: <Route />, path: '/route-planning', color: '#9c27b0' },
    { title: 'Manage App Versions', icon: <SystemUpdate />, path: '/app-versions', color: '#ff9800' },
    { title: 'View Analytics', icon: <Analytics />, path: '/analytics', color: '#1565c0' },
  ];

  const recentActivities = [
    { action: 'Added new station', entity: 'Andheri Station', time: '2 hours ago' },
    { action: 'Updated line', entity: 'Line 1 (Versova-Andheri-Ghatkopar)', time: '4 hours ago' },
    { action: 'Added facility', entity: 'Airport connection at Versova', time: '6 hours ago' },
    { action: 'Planned route', entity: 'Versova to Ghatkopar', time: '1 day ago' },
  ];

  return (
    <Container maxWidth={false} sx={{ height: '100%', p: 0 }}>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4, px: 3 }}>
          Dashboard Overview
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4, px: 3 }}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <LocationCity sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.cities}
                </Typography>
                <Typography color="text.secondary">Cities</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Train sx={{ fontSize: 40, color: '#dc004e', mb: 1 }} />
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.lines}
                </Typography>
                <Typography color="text.secondary">Lines</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Place sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.stations}
                </Typography>
                <Typography color="text.secondary">Stations</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <AccountTree sx={{ fontSize: 40, color: '#ed6c02', mb: 1 }} />
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.connections}
                </Typography>
                <Typography color="text.secondary">Connections</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <LocalHospital sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.facilities}
                </Typography>
                <Typography color="text.secondary">Facilities</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Accessibility sx={{ fontSize: 40, color: '#1565c0', mb: 1 }} />
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.amenities}
                </Typography>
                <Typography color="text.secondary">Amenities</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <SystemUpdate sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.appVersions}
                </Typography>
                <Typography color="text.secondary">App Versions</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  {quickActions.map((action) => (
                    <Grid item xs={6} key={action.title}>
                      <Button
                        variant="outlined"
                        startIcon={action.icon}
                        fullWidth
                        onClick={() => navigate(action.path)}
                        sx={{
                          justifyContent: 'flex-start',
                          borderColor: action.color,
                          color: action.color,
                          '&:hover': {
                            borderColor: action.color,
                            backgroundColor: `${action.color}10`,
                          },
                        }}
                      >
                        {action.title}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activities */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Recent Activities
                </Typography>
                <List>
                  {recentActivities.map((activity, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemIcon>
                          <Add color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.action}
                          secondary={`${activity.entity} • ${activity.time}`}
                        />
                      </ListItem>
                      {index < recentActivities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* System Status */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  System Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e8' }}>
                      <Typography variant="h6" color="success.main">
                        Operational
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        All systems running
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                      <Typography variant="h6" color="warning.main">
                        45 Stations
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active stations
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                      <Typography variant="h6" color="info.main">
                        8 Lines
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Metro lines
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                      <Typography variant="h6" color="secondary.main">
                        120 Routes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Available routes
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Dashboard; 